/**
 * REGISTER A TRUST (Phase 3). The platform does not FORM a trust: that happens with counsel, at a
 * registry, in the real world, and takes real-world time. This records that the trust exists and holds
 * the asset, so tokens can be issued against it. UI-first: the submit is simulated.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { api, LipaApiError } from '@/lib/api';

const KINDS = [
  { value: 'trust', label: 'Trust', hint: 'A trust holds the asset for beneficiaries.' },
  { value: 'spv', label: 'SPV', hint: 'A special-purpose vehicle that owns the asset.' },
  { value: 'series', label: 'Series LLC', hint: 'A series within an LLC, per asset.' },
  { value: 'company', label: 'Company', hint: 'A limited company that holds the asset.' },
];

export default function RegisterTrust() {
  const navigate = useNavigate();
  const [kind, setKind] = useState('trust');
  const [legalName, setLegalName] = useState('');
  const [jurisdiction, setJurisdiction] = useState('Kenya');
  const [regNumber, setRegNumber] = useState('');
  const [trustee, setTrustee] = useState('');
  const [asset, setAsset] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.registerTrust({
        kind,
        legal_name: legalName,
        jurisdiction,
        registration_number: regNumber,
        trustee,
        asset,
      });
      navigate('/trust');
    } catch (err) {
      setError(err instanceof LipaApiError ? err.detail.message : 'We could not register that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg space-y-5">
      <Link
        to="/trust"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
        </svg>
        Trust entities
      </Link>

      <header>
        <h1 className="text-page-title text-ink">Register a trust</h1>
        <p className="mt-0.5 text-sm text-ink-muted">
          Record the legal structure that holds your asset. Tokens are issued against it.
        </p>
      </header>

      <form
        onSubmit={submit}
        className="space-y-5 rounded-xl border border-border bg-surface p-6 shadow-card"
      >
        {error ? (
          <div
            role="alert"
            className="rounded-field border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
          >
            {error}
          </div>
        ) : null}

        <fieldset>
          <legend className="text-sm font-medium text-ink">Structure type</legend>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {KINDS.map((k) => (
              <button
                type="button"
                key={k.value}
                onClick={() => setKind(k.value)}
                className={`rounded-field border p-3 text-left transition ${
                  kind === k.value
                    ? 'border-primary bg-primary-tint'
                    : 'border-border hover:bg-surface-sunken'
                }`}
              >
                <span
                  className={`block text-sm font-medium ${kind === k.value ? 'text-brand' : 'text-ink'}`}
                >
                  {k.label}
                </span>
                <span className="mt-0.5 block text-xs text-ink-muted">{k.hint}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <Field
          label="Legal name"
          id="name"
          value={legalName}
          onChange={setLegalName}
          placeholder="Block 12 Plot 4471 Trust"
          required
        />
        <Field
          label="Asset it holds"
          id="asset"
          value={asset}
          onChange={setAsset}
          placeholder="Land at LR/NRB/BLOCK-12/PLOT-4471"
          required
        />
        <Field
          label="Trustee"
          id="trustee"
          value={trustee}
          onChange={setTrustee}
          placeholder="Zoe Fiduciary Services Ltd"
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Jurisdiction"
            id="jur"
            value={jurisdiction}
            onChange={setJurisdiction}
            required
          />
          <Field
            label="Registration no."
            id="reg"
            value={regNumber}
            onChange={setRegNumber}
            placeholder="KE-TR-2026-0042"
            required
            mono
          />
        </div>

        <p className="rounded-field bg-surface-sunken p-3 text-xs leading-relaxed text-ink-muted">
          We do not form the trust for you. Register it with counsel and the relevant registry
          first, then record it here so ownership is verifiable before any token is issued.
        </p>

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:opacity-50"
        >
          {busy ? 'Registering...' : 'Register trust'}
        </button>
      </form>
    </div>
  );
}

function Field({
  label,
  id,
  value,
  onChange,
  placeholder,
  required,
  mono,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  mono?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className={`mt-1 w-full rounded-field border border-border bg-surface px-3 py-2.5 text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25 ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}
